import React from 'react';
import { useRevenueManagerBranches } from '../../hooks/use-properties';
import type { RevenueManagerBranch } from '../../types/revenue-manager';

interface BranchSelectorProps {
  onBranchSelect?: (branch: RevenueManagerBranch) => void;
  selectedBranchId?: number;
}

/**
 * Example component demonstrating how to use the Revenue Manager Branch List API
 * 
 * Usage:
 * ```tsx
 * import { BranchSelector } from '@mettastay/revenue-manager';
 * 
 * function MyComponent() {
 *   const handleBranchSelect = (branch) => {
 *     console.log('Selected branch:', branch);
 *   };
 *   
 *   return <BranchSelector onBranchSelect={handleBranchSelect} />;
 * }
 * ```
 */
export function BranchSelector({ onBranchSelect, selectedBranchId }: BranchSelectorProps) {
  const { data, isLoading, error } = useRevenueManagerBranches();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="text-sm text-gray-500">Loading branches...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-sm text-red-600">Failed to load branches. Please try again.</p>
      </div>
    );
  }

  if (!data?.data || data.data.length === 0) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
        <p className="text-sm text-gray-600">No branches found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Select Property
      </label>
      <select
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        value={selectedBranchId || ''}
        onChange={(e) => {
          const branchId = parseInt(e.target.value);
          const branch = data.data.find(b => b.id === branchId);
          if (branch && onBranchSelect) {
            onBranchSelect(branch);
          }
        }}
      >
        <option value="">Select a property...</option>
        {data.data.map((branch) => (
          <option key={branch.id} value={branch.id}>
            {branch.branch_name}
          </option>
        ))}
      </select>
      
      {selectedBranchId && (
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-xs text-gray-600">
            <span className="font-medium">Property ID:</span>{' '}
            {data.data.find(b => b.id === selectedBranchId)?.cm_property_id}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Direct API usage example (without React hook)
 * 
 * ```tsx
 * import { fetchRevenueManagerBranchList } from '@mettastay/revenue-manager';
 * 
 * async function loadBranches() {
 *   try {
 *     const response = await fetchRevenueManagerBranchList();
 *     if (response.success) {
 *       console.log('Branches:', response.data);
 *     }
 *   } catch (error) {
 *     console.error('Failed to fetch branches:', error);
 *   }
 * }
 * ```
 */
